import React from 'react';
import { KatInput, KatLabel, KatBox, KatDropdown, KatAlert } from '@amzn/katal-react';
import { CustomEventHandler, EventHandler } from '../utils/customEventHandler'
import forge from 'node-forge'
import * as constants from '../appConstants';
import initialMetricsPublisher from '../metrics';

import './App.scss';

export interface IAuthenticationContentProps  {
    authValues: {
        region: string,
        publicKeyId: string,
        privateKey: forge.pki.rsa.PrivateKey,
        authToken: string
    }
}

function EnvironmentErrorBanner(state) {
    if (state.environmentError) {
        return(
            <div>
                <KatAlert id="sandboxAlert"
                    description="This tool is only compatible with Sandbox environment keys."
                    header="Unsupported API key type detected"
                    variant="danger"
                    persistent
                />
                <br/>
            </div>
        );
    } else {
        return null;
    }
}

function KeyLoadSuccessBanner(state) {
    const bitLength = state.keyLoaded.n ? state.keyLoaded.n.bitLength() : 0;
    if (bitLength > 0) {
        const message = bitLength + "-bit RSA private key successfully loaded";
        return(
            <div>
                <KatAlert id="keySuccess"
                    description={message}
                    header="Private key loaded"
                    variant="success"
                    persistent
                />
                <br/>
            </div>
        );
    } else {
        return null;
    }
}

export class AuthenticationContent extends React.Component<IAuthenticationContentProps> {
    eventHandler: CustomEventHandler;

    constructor(IAuthenticationContentProps: Readonly<IAuthenticationContentProps>) {
        super(IAuthenticationContentProps);
        this.state = {
            region: this.props.authValues.region,
            publicKeyId: this.props.authValues.publicKeyId,
            privateKey: this.props.authValues.privateKey,
            authToken: this.props.authValues.authToken,
            environmentError: false
        };
        this.handleInputChange = this.handleInputChange.bind(this);
        this.eventHandler = EventHandler;
        this.loadPrivateKey = this.loadPrivateKey.bind(this);
    }

    handleInputChange(event: { target: any; }) {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        // if the user modifies their Public Key ID, be sure to clear out any previously loaded private key
        if (name === "publicKeyId") {
           if (value.trim().length == 0) {
               this.setState({environmentError: false, privateKey: {}});
           } else {
               this.setState({environmentError: !value.toUpperCase().startsWith("SANDBOX-"), privateKey: {}});
           }
        }

        this.setState({
            [name]: value
        });
        this.eventHandler.emit('authEvent', this.state);
    }

    async loadPrivateKey(event: { target: any; }) {
        const target = event.target;
        const file = target.files[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.readAsText(file);
        const contents = await new Promise((resolve, reject) => {
            reader.onload = function(event) {
                resolve(reader.result)
            }
        })

        let privateKey = {} as forge.pki.rsa.PrivateKey;
        try {
            privateKey = forge.pki.privateKeyFromPem(contents as string);
        } catch (err) {
            alert("The selected file is not a private key.\n\n" + err);
        }
        this.setState({privateKey: privateKey});
        this.eventHandler.emit('authEvent', this.state);
    }

    privateKeyLoadButton(keyId) {
        let disabled = true;
        if (keyId.toUpperCase().startsWith("SANDBOX-")) {
            disabled = false;
        }
        return (
            <div>
                <KatLabel text="Private Key *" id="authInput" /><br/>
                <input disabled={disabled} id="file-upload" type="file" required onChange={this.loadPrivateKey} /><br/><br/>
            </div>
       );
    }

    render() {
	const actionMetricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod("AuthenticationPage");
        actionMetricsPublisher.publishCounterMonitor('visits', 1);

        return (
            <KatBox>
                <EnvironmentErrorBanner environmentError={this.state["environmentError"]} />

                <KatDropdown
                    label="Region"
                    options={constants.REGIONS}
                    name="region"
                    value={this.state["region"]}
                    onChange={this.handleInputChange}
                    id="authInput"
                    disabled
	            tooltipText="Region is locked by the Scratchpad domain.  Change domain to pay-api.amazon.com, pay-api.amazon.eu, or pay-api.amazon.jp to switch regions."
                />
                <br/>

                <KatInput
                    label="Public Key ID *"
                    id="authInput"
                    required type="text"
                    name="publicKeyId"
                    value={this.state["publicKeyId"]}
                    onChange={this.handleInputChange}
                />

                <br/>

                {this.privateKeyLoadButton(this.state["publicKeyId"])}

                <KeyLoadSuccessBanner keyLoaded={this.state["privateKey"]} />

                <KatAlert
                    description="This tool is only compatible with Sandbox environment API keys.  Always safeguard your private keys against unauthorized disclosure.  Never hard-code or embed keys in your code."
                    header="Important!"
                    variant="warning"
                    persistent
                />
                <br/>

                <KatInput
                    label="Auth Token"
                    id="authInput"
                    tooltipText="Authorization token used to make delegated API calls on behalf of a merchant.  This value is passed in the header key X-Amz-Pay-AuthToken."
                    placeholder="x-amz-pay-authtoken"
                    name="authToken"
                    value={this.state["authToken"]}
                    onChange={this.handleInputChange}
                />
            </KatBox>
        );
    }
}

export default AuthenticationContent;
